import type { MouseEvent } from "react";
import { createColumnHelper } from "@tanstack/react-table";
import { Badge } from "@/components/ui/badge";
import { DocNumberLink } from "@/modules/core/components/DocNumberLink";
import type { DeskTableFeatures } from "@/modules/shell/data-table";
import type { ProductRow } from "@/shared/api/inventory";
import { RedactedMoney } from "@/shared/feedback/redacted-value";

const columnHelper = createColumnHelper<DeskTableFeatures, ProductRow>();

export type ProductColumnsOpts = {
  companySlug: string;
  /**
   * Desk glance peek — plain click opens peek; ⌘/Ctrl/middle-click still
   * navigates to the full detail route (plan/40).
   */
  onGlance?: (productId: string) => void;
};

function openProductLink(
  e: MouseEvent<HTMLAnchorElement>,
  productId: string,
  onGlance?: (productId: string) => void,
) {
  if (!onGlance) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) {
    return;
  }
  e.preventDefault();
  onGlance(productId);
}

export function productColumns(opts: ProductColumnsOpts | string) {
  const companySlug = typeof opts === "string" ? opts : opts.companySlug;
  const onGlance = typeof opts === "string" ? undefined : opts.onGlance;

  return columnHelper.columns([
    columnHelper.accessor("name", {
      id: "name",
      header: "Name",
      size: 220,
      cell: (info) => {
        const p = info.row.original;
        return (
          <div className="flex flex-wrap items-center gap-1.5">
            <DocNumberLink
              to={`/c/${companySlug}/products/${p.id}`}
              className="font-sans"
              onClick={(e) => openProductLink(e, p.id, onGlance)}
            >
              {info.getValue()}
            </DocNumberLink>
            {p.deactivatedAt != null ? (
              <Badge variant="outline">deactivated</Badge>
            ) : null}
          </div>
        );
      },
    }),
    columnHelper.accessor("sku", {
      id: "sku",
      header: "SKU",
      size: 120,
      cell: (info) => {
        const sku = info.getValue();
        if (!sku) return "—";
        return (
          <DocNumberLink
            to={`/c/${companySlug}/products/${info.row.original.id}`}
            onClick={(e) =>
              openProductLink(e, info.row.original.id, onGlance)
            }
          >
            {sku}
          </DocNumberLink>
        );
      },
    }),
    columnHelper.accessor("barcode", {
      id: "barcode",
      header: "Barcode",
      size: 140,
      enableSorting: false,
      cell: (info) => info.getValue() || "—",
    }),
    columnHelper.accessor("kind", {
      id: "kind",
      header: "Kind",
      size: 100,
      cell: (info) => (
        <Badge
          variant={info.getValue() === "service" ? "secondary" : "primary"}
        >
          {info.getValue()}
        </Badge>
      ),
    }),
    columnHelper.accessor("avgCost", {
      id: "avgCost",
      header: "Avg cost",
      size: 110,
      meta: { align: "right" },
      cell: (info) => <RedactedMoney value={info.getValue()} />,
    }),
  ]);
}
